import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom';
import { ROUTES } from '@aurora-pvi/contracts';

interface RouteErrorProps {
  notFound?: boolean;
}

/**
 * Route-level error boundary.
 *
 * A 403 renders the same way as a 404 on purpose. UX-S07 requires that an
 * unauthorized response reveal neither the existence of an entity nor its name
 * or count, and a distinguishable "you may not see this" message is exactly the
 * leak that rule exists to prevent.
 */
export function RouteError({ notFound = false }: RouteErrorProps) {
  const error = useRouteError();

  const isDenied =
    notFound || (isRouteErrorResponse(error) && (error.status === 404 || error.status === 403));

  const title = isDenied ? 'This page is not available' : 'Something went wrong';
  const body = isDenied
    ? 'The page you requested does not exist, or is outside your current span of control.'
    : 'The page could not be rendered. The error has been logged with a correlation ID.';

  const correlationId =
    isRouteErrorResponse(error) && typeof error.data === 'object' && error.data
      ? (error.data as { correlationId?: string }).correlationId
      : undefined;

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md rounded-lg border border-[var(--color-border)] bg-white p-8 text-center shadow-sm">
        <h1 className="mb-2 text-xl font-semibold text-[var(--color-text-primary)]">{title}</h1>
        <p className="mb-6 text-sm text-[var(--color-text-secondary)]">{body}</p>

        {correlationId && (
          <p className="mb-6 font-mono text-xs text-[var(--color-text-tertiary)]">
            Correlation ID: {correlationId}
          </p>
        )}

        <Link
          to={ROUTES.portfolioHierarchy}
          className="inline-flex items-center rounded-md bg-[var(--color-action)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-action)]"
        >
          Return to portfolio
        </Link>

        {import.meta.env.DEV && error instanceof Error && (
          <pre className="mt-6 overflow-x-auto rounded bg-[var(--color-canvas)] p-3 text-left text-xs text-[var(--color-text-tertiary)]">
            {error.stack ?? error.message}
          </pre>
        )}
      </div>
    </div>
  );
}
